import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FormError, FormField } from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDateString } from "@/lib/format-date"
import {
  getDefaultPhoneNumber,
  getDefaultServiceFollowup,
  getDefaultSpecialtyFollowup,
  getDefaultUrgencyFollowup,
  resolveAddress,
} from "@/modules/patients/agenda/teleconsultation/booking/hooks/useDefaultValues"
import { isChatSelectable } from "@/modules/patients/agenda/teleconsultation/booking/libs/teleconsultation-helper"
import { TeleconsultationMode } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import {
  PartnerUser,
  TeleconsultationForm,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card.tsx"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"

export const FollowUpSection = ({
  mode,
  followUpProgressNotes,
  countryPatient,
  partners,
}: {
  mode: TeleconsultationMode
  followUpProgressNotes: ProgressNoteAndParentData[]
  countryPatient: NonNullable<PatientData>
  partners: PartnerUser[]
}) => {
  const { t } = useTranslation()
  const callerPhoneNumber = mode.kind === "EDITING_APPOINTMENT" ? "" : (mode.data.callerPhoneNumber ?? "")

  const {
    control,
    formState: { errors: formErrors },
    reset,
    getValues,
  } = useFormContext<TeleconsultationForm>()

  const error = t(formErrors?.followUpForProgressNoteId?.message ?? "")

  const handleFollowUpChange = (followUpId: string) => {
    const progressNote = followUpProgressNotes.find((item) => item.episodeOfCareProgressNoteData.id === followUpId)

    if (!progressNote) return

    const { appointment: precedingAppointment, createdBy } = progressNote.episodeOfCareProgressNoteData
    const { language, service, specialty, channel, reason, patientAddress, medgateConnectChannel, partnerUserEmail } =
      precedingAppointment
    const partner = partners.find((p) => p.email === partnerUserEmail)
    const followUpService = getDefaultServiceFollowup(service)
    const isAsyncRequestForNewAppointment = mode.kind === "ADD_APPOINTMENT" && channel === "ASYNC_REQUEST"
    const isChatNotSelectableForFollowUp =
      mode.kind !== "EDITING_APPOINTMENT" &&
      channel === "CHAT" &&
      !isChatSelectable({
        contactChannel: countryPatient.contactChannel,
        service: followUpService,
        isMedicalChatEnabled: countryPatient.medgateServiceInfo.isMedicalChatEnabled,
      })

    const followUpValues: Partial<TeleconsultationForm> = {
      // Agent
      agentUserName: createdBy,
      appointmentType: "FOLLOW_UP",
      patientPhoneNumber: getDefaultPhoneNumber(
        { kind: "FOLLOWUP_FOR_PROGRESS_NOTE", data: { progressNote: progressNote.episodeOfCareProgressNoteData } },
        partner?.phoneNumber,
        callerPhoneNumber,
        countryPatient.phoneNumber,
      ),

      // Service
      channel: isAsyncRequestForNewAppointment || isChatNotSelectableForFollowUp ? "PHONE" : channel,
      language,
      reason,
      specialty: getDefaultSpecialtyFollowup(service, specialty),
      service: followUpService,
      urgency: getDefaultUrgencyFollowup(service, "NORMAL"),

      // Address
      ...resolveAddress(patientAddress, medgateConnectChannel, precedingAppointment.patientPhoneNumber),

      // Medgate Connect
      assignedPartner: partnerUserEmail ?? null,
      partnerType: partner?.userTag ?? null,
      isPartnerDoc2U: partner?.isDoc2U ?? null,
    }

    reset({ ...getValues(), ...followUpValues }, { keepDirty: true })
  }

  return (
    <FormField
      control={control}
      name="followUpForProgressNoteId"
      render={({ field: { value, onChange, name } }) => (
        <div id={name}>
          <Select
            value={value}
            onValueChange={(v) => {
              onChange(v)
              handleFollowUpChange(v)
            }}>
            <div>
              <SelectTrigger data-testid="follow-up-for-select-trigger" hasError={!!error}>
                <SelectValue placeholder={t("patient.agenda.followUpFor")}>
                  <LabelAndSelectValue
                    label={t("patient.agenda.followUpFor")}
                    value={formatDateString(
                      followUpProgressNotes.find((item) => item.episodeOfCareProgressNoteData.id === value)
                        ?.episodeOfCareProgressNoteData.createdAt,
                    )}
                  />
                </SelectValue>
              </SelectTrigger>
              {error && <FormError>{error}</FormError>}
            </div>
            <SelectContent className="max-w-(--radix-select-trigger-width)">
              <ScrollArea className="max-h-78.75" data-testid="follow_up_item_container">
                <div className="py-2">
                  {followUpProgressNotes.map(
                    ({
                      episodeOfCareProgressNoteData: { id, createdAt },
                      parentEpisodeOfCare: {
                        isVirtual,
                        icdDiagnosis: { text },
                      },
                    }) => (
                      <SelectItem key={id} value={id}>
                        <p className="text-on-surface text-sm font-normal" data-testid="follow_up_progress_note_item">
                          {formatDateString(createdAt)}
                        </p>
                        {!isVirtual && text && (
                          <p className="text-on-surface-subdued text-xs tracking-[.25px]">{text}</p>
                        )}
                      </SelectItem>
                    ),
                  )}
                </div>
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
      )}
    />
  )
}
